import { useEffect, useRef, useState } from "react";
import { Device } from "@twilio/voice-sdk";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { decodeMulawBase64 } from "@/lib/mulawDecode.mjs";
import { Button } from "@/components/ui/button";
import { Loader2, Headphones, PhoneOff, Ear, EarOff } from "lucide-react";
import { toast } from "sonner";

const LIVE_STATUSES = new Set(["auto_dialing", "auto_active", "auto_transferring", "dialing", "active", "human_takeover"]);

function fmtElapsed(sec) {
  const s = Math.max(0, Number(sec) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

/** Live-call panel for exactly one Sophia session (Phase 1 section D).
 * Listen Live plays real audio: the backend relays Twilio's read-only media
 * stream over a second websocket to this browser, and this component decodes
 * mu-law and plays it through the Web Audio API. It is structurally
 * listen-only -- there is no code anywhere in this component, or in the
 * backend relay it connects to, that sends audio TO the call, so the client
 * and Sophia can never hear a supervisor who is only listening. Starting
 * playback requires an explicit click (the AudioContext is created inside
 * the click handler); opening this panel alone connects no audio. */
export function SophiaLiveCallPanel({ sessionId, onClose }) {
  const [live, setLive] = useState(null);
  const [confirming, setConfirming] = useState(""); // "" | "takeover" | "end"
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [listenBusy, setListenBusy] = useState(false);
  const deviceRef = useRef(null);
  const listenWsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const nextPlayTimeRef = useRef(0);

  const load = () => api.get(`/voice-ai/auto/sessions/${sessionId}/live`)
    .then(({ data }) => setLive(data)).catch(() => {});

  useEffect(() => {
    let alive = true;
    load();
    const interval = setInterval(() => { if (alive) load(); }, 4000);
    return () => { alive = false; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => () => {
    try { deviceRef.current?.destroy(); } catch (e) {}
    try { listenWsRef.current?.close(); } catch (e) {}
    try { audioCtxRef.current?.close(); } catch (e) {}
  }, []);

  const startListening = async () => {
    setListenBusy(true);
    try {
      const { data } = await api.post(`/voice-ai/auto/sessions/${sessionId}/listen/start`);
      const AudioContextCls = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContextCls();
      audioCtxRef.current = ctx;
      nextPlayTimeRef.current = ctx.currentTime;
      const wsUrl = `${API.replace(/^http/, "ws")}/voice-ai/auto/listen-media/${sessionId}/browser?token=${encodeURIComponent(data.listen_token)}`;
      const ws = new WebSocket(wsUrl);
      listenWsRef.current = ws;
      ws.onmessage = (evt) => {
        let msg;
        try { msg = JSON.parse(evt.data); } catch (e) { return; }
        if (msg.event !== "media" || !msg.payload) return;
        const samples = decodeMulawBase64(msg.payload);
        if (!samples.length) return;
        const buffer = ctx.createBuffer(1, samples.length, 8000);
        buffer.copyToChannel(samples, 0);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        // Schedule frames back-to-back for gapless playback; if we've fallen
        // behind (a network hiccup), resync to "now" instead of catching up.
        const startAt = Math.max(nextPlayTimeRef.current, ctx.currentTime);
        source.start(startAt);
        nextPlayTimeRef.current = startAt + buffer.duration;
      };
      ws.onclose = () => setListening(false);
      ws.onerror = () => toast.error("Listen Live connection dropped.");
      setListening(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not start Listen Live");
    } finally {
      setListenBusy(false);
    }
  };

  const stopListening = async () => {
    setListenBusy(true);
    try { listenWsRef.current?.close(); } catch (e) {}
    listenWsRef.current = null;
    try { audioCtxRef.current?.close(); } catch (e) {}
    audioCtxRef.current = null;
    setListening(false);
    try {
      await api.post(`/voice-ai/auto/sessions/${sessionId}/listen/stop`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not stop Listen Live on the server");
    } finally {
      setListenBusy(false);
    }
  };

  const takeOver = async () => {
    setBusy(true);
    try {
      if (listening) await stopListening();
      await api.post(`/voice-ai/auto/sessions/${sessionId}/takeover`, { confirm: true });
      toast.success("Sophia's audio has been stopped. Connecting you to the call...");
      setConfirming("");
      load();
      const { data: t } = await api.post("/voice/token");
      const device = new Device(t.token, { logLevel: 1 });
      deviceRef.current = device;
      device.on("error", () => toast.error("Your audio connection dropped. Take Over again to reconnect."));
      await device.connect({ params: { SupervisorJoin: sessionId } });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not take over this call");
    } finally { setBusy(false); }
  };

  const endCall = async () => {
    setBusy(true);
    try {
      try { deviceRef.current?.destroy(); } catch (e) {}
      await api.post(`/voice-ai/auto/sessions/${sessionId}/end-call`, { confirm: true });
      toast.success("Call ended");
      setConfirming("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not end this call");
    } finally { setBusy(false); }
  };

  if (!live) return null;
  const isLive = LIVE_STATUSES.has(live.status);

  return (
    <div className="mt-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-xs" data-testid="sophia-live-call-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-medium text-emerald-900">
          Live Call — {live.client_name}
          {live.test_mode && (
            <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-900" data-testid="sophia-live-test-mode-badge">
              Test Mode
            </span>
          )}
        </div>
        {onClose && <button type="button" className="text-emerald-700 underline" onClick={onClose}>Close</button>}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-emerald-900">
        <div>Phone: {live.phone}</div>
        <div>Elapsed: {fmtElapsed(live.elapsed_seconds)}</div>
        <div>Status: {live.status}</div>
        <div>Provider status: {live.twilio_call_status || "—"}</div>
        <div>Purpose: {live.purpose || "—"}</div>
        <div>Identity: {live.communication_identity || "—"}</div>
        <div>Recording: {live.recording ? "Yes" : "No"}{live.recording_disclosure_required ? " (disclosed)" : ""}</div>
        <div>{live.human_control ? `Human control: ${live.takeover_by || "yes"}` : "Sophia is speaking"}</div>
      </div>

      {isLive && !live.human_control && (
        <div className="mt-2">
          {listening ? (
            <Button size="sm" variant="outline" disabled={listenBusy} onClick={stopListening} data-testid="sophia-live-stop-listening">
              {listenBusy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <EarOff className="mr-1 h-3 w-3" />} Stop Listening
            </Button>
          ) : (
            <Button size="sm" variant="outline" disabled={listenBusy} onClick={startListening} data-testid="sophia-live-listen">
              {listenBusy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Ear className="mr-1 h-3 w-3" />} Listen Live
            </Button>
          )}
          {listening && (
            <span className="ml-2 text-emerald-700" data-testid="sophia-live-listening-badge">● Listening (muted — read-only)</span>
          )}
        </div>
      )}

      {isLive && !live.human_control && (
        <div className="mt-2">
          {confirming === "takeover" ? (
            <div className="flex gap-2">
              <Button size="sm" variant="destructive" disabled={busy} onClick={takeOver} data-testid="sophia-live-confirm-takeover">
                {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null} Confirm Take Over (stops Sophia)
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirming("")}>Cancel</Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setConfirming("takeover")} data-testid="sophia-live-takeover">
              <Headphones className="mr-1 h-3 w-3" /> Join / Take Over
            </Button>
          )}
        </div>
      )}

      {isLive && (
        <div className="mt-2">
          {confirming === "end" ? (
            <div className="flex gap-2">
              <Button size="sm" variant="destructive" disabled={busy} onClick={endCall} data-testid="sophia-live-confirm-end">
                {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null} Confirm End Call
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirming("")}>Cancel</Button>
            </div>
          ) : (
            <Button size="sm" variant="ghost" className="text-red-700" onClick={() => setConfirming("end")} data-testid="sophia-live-end">
              <PhoneOff className="mr-1 h-3 w-3" /> End Active Call
            </Button>
          )}
        </div>
      )}

      {!isLive && <div className="mt-2 text-emerald-800">This call is no longer active ({live.status}).</div>}
    </div>
  );
}
